import React, { useEffect, useCallback, useState, useRef, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { Box, Button, Stack, Typography, Divider } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { randomId } from '@mui/x-data-grid-generator'
import { useGridApiRef } from '@mui/x-data-grid-pro'
import { useForm } from 'react-hook-form'
import Input from 'components/generic/form/Input'
import Select from 'components/generic/form/Select'
import RowActionsCell from 'components/generic/datagrid/cells/RowActionsCell'
import BasicDataGrid from 'components/generic/datagrid'
import { formOptions } from 'utils/validation/deduction'
import { roundTo } from 'utils/data-manipulation/valueFormatter'
import { ADJUSTMENT_TYPES, UNIT_IDS, UNITS_ARRAY, ADJUSTMENT_TYPES_ARRAY } from 'utils/constants'
import DialogModal from '.'

const determineNumberOfDecimals = (type, unit, showWeightDecimals = false) => {
  if (unit === 'percentage') return 1
  if (type === 'weight') return showWeightDecimals ? 1 : 0
  if (type === 'cost') return 2
  return 0
}

const columns = (showWeightDecimals, pricePrepend) => [
  {
    field: 'typeText',
    headerName: 'Type',
    flex: 1,
    sortable: false,
  },
  {
    field: 'descriptionText',
    headerName: 'Description',
    flex: 2,
    sortable: false,
  },
  {
    field: 'amount',
    headerName: 'Amount',
    flex: 1,
    sortable: false,
    valueGetter: ({ row }) => {
      const { amount, type, unit } = row
      const decimals = determineNumberOfDecimals(type, unit, showWeightDecimals)
      return roundTo(amount, decimals).toFixed(decimals)
    },
  },
  {
    field: 'unitText',
    headerName: 'UM',
    flex: 1,
    sortable: false,
    valueGetter: ({ row }) => {
      const { unitText } = row
      return unitText === '$' ? pricePrepend : unitText
    },
  },
  {
    field: 'notes',
    headerName: 'Notes',
    flex: 1,
    sortable: false,
  },
  {
    field: 'actions',
    headerName: 'Actions',
    type: 'actions',
    flex: 1,
    renderCell: RowActionsCell,
  },
]

const formatWithDefaultUm = (unit, defaultYardUm) => {
  if (unit.id === UNIT_IDS.POUND) return { ...unit, name: defaultYardUm, label: defaultYardUm }
  return unit
}

const AddAdjustmentForm = ({ onSubmit, listOfAdjustments, pricePrepend }) => {
  const { handleSubmit, control, watch, formState, setValue, getValues } = useForm(formOptions)
  const { errors } = formState

  const { defaultYardUm } = useSelector(state => state.settings)

  const FORMATTED_UNITS_ARRAY = useMemo(
    () => UNITS_ARRAY.map(unit => formatWithDefaultUm(unit, defaultYardUm)),
    [UNITS_ARRAY, defaultYardUm],
  )

  const [selectedUnits, setSelectedUnits] = useState(FORMATTED_UNITS_ARRAY)

  const adjustmentTypeValue = watch('type', ADJUSTMENT_TYPES.WEIGHT)
  const descriptionId = watch('description')
  const unitId = watch('unit')

  useEffect(() => {
    const adjustment = listOfAdjustments?.find(({ id }) => id === descriptionId)
    if (adjustment?.adjustmentType) setValue('type', adjustment.adjustmentType)
    if (adjustment?.display) {
      setValue('descriptionText', adjustment.display)
      setValue('notes', adjustment.display)
    }
    if (adjustment?.amount) setValue('amount', adjustment.amount)
    if (adjustment?.um) setValue('unit', adjustment?.um)
  }, [descriptionId])

  useEffect(() => {
    const newSelectedUnits = FORMATTED_UNITS_ARRAY.filter(unit => {
      const { id } = unit
      return adjustmentTypeValue === ADJUSTMENT_TYPES.WEIGHT ? id !== UNIT_IDS.USD : id !== UNIT_IDS.POUND
    }).map(unit => {
      if (unit.id !== UNIT_IDS.USD) return unit
      return { ...unit, name: pricePrepend }
    })
    setSelectedUnits(newSelectedUnits)
    if (!newSelectedUnits.find(({ id }) => id === unitId)) {
      setValue('unit', newSelectedUnits[0].id)
    }
  }, [adjustmentTypeValue, pricePrepend, defaultYardUm])

  const presets = listOfAdjustments.filter(({ amount, um, adjustmentType }) => amount || um || adjustmentType)
  const descriptions = listOfAdjustments.filter(({ amount, um, adjustmentType }) => !amount && !um && !adjustmentType)

  const groupedOptions = []
  if (presets?.length) {
    groupedOptions.push({ name: 'Presets', isHeader: true }, ...presets)
  }
  if (descriptions.length) {
    groupedOptions.push({ name: 'Description', isHeader: true }, ...descriptions)
  }

  const onAfterSubmit = () => {
    // reset notes to the description text to remove user edits
    setValue('notes', getValues('descriptionText'))
  }

  return (
    <>
      <Stack py={1} direction="row" spacing={1} borderBottom={1} borderColor="divider">
        <Typography flex={1}>Type</Typography>
        <Typography flex={2}>Description</Typography>
        <Typography flex={1}>Amount</Typography>
        <Typography flex={1}>UM</Typography>
        <Typography flex={1}>Notes</Typography>
        <Box flex={1} />
      </Stack>
      <form
        onSubmit={e => {
          e.stopPropagation()
          handleSubmit(onSubmit)(e)
          onAfterSubmit()
        }}
      >
        <Stack py={1} direction="row" spacing={1}>
          <Box flex={1}>
            <Select
              name="type"
              variant="standard"
              defaultValue={ADJUSTMENT_TYPES.WEIGHT}
              control={control}
              items={ADJUSTMENT_TYPES_ARRAY}
            />
          </Box>
          <Box flex={2}>
            <Select
              name="description"
              variant="standard"
              defaultValue={listOfAdjustments?.[0]?.id || ''}
              control={control}
              items={groupedOptions}
            />
          </Box>
          <Box flex={1}>
            <Input
              name="amount"
              variant="standard"
              defaultValue=""
              control={control}
              onClick={event => {
                event.stopPropagation()
              }}
              error={!!errors.amount}
              helperText={errors.amount?.message}
              type="number"
            />
          </Box>
          <Box flex={1}>
            <Select
              name="unit"
              variant="standard"
              defaultValue={selectedUnits[0].id}
              control={control}
              items={selectedUnits}
            />
          </Box>
          <Box flex={1}>
            <Input
              name="notes"
              variant="standard"
              defaultValue=""
              control={control}
              onClick={event => {
                event.stopPropagation()
              }}
            />
          </Box>
          <Stack flex={1} alignItems="center">
            <Button type="submit" variant="outlined" startIcon={<AddIcon />}>
              Add
            </Button>
          </Stack>
        </Stack>
      </form>
    </>
  )
}

AddAdjustmentForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  listOfAdjustments: PropTypes.array,
  pricePrepend: PropTypes.string,
}

AddAdjustmentForm.defaultProps = {
  listOfAdjustments: [],
  pricePrepend: '$',
}

const AdjustmentModal = ({ onSave, adjustments, pricePrepend, ...restProps }) => {
  const apiRef = useGridApiRef()

  const { defaultYardUm, settings } = useSelector(state => state.settings)
  const { showWeightDecimals } = settings?.purchaseSettings?.inboundTickets || {}
  const listOfAdjustments = useRef([])

  const FORMATTED_UNITS_ARRAY = useMemo(
    () => UNITS_ARRAY.map(unit => formatWithDefaultUm(unit, defaultYardUm)),
    [UNITS_ARRAY, defaultYardUm],
  )

  useEffect(() => {
    listOfAdjustments.current = (settings?.purchaseSettings?.deductions?.listOfDeductions || [])
      .filter(item => !!item?.active)
      .map(item => ({
        ...item,
        name: item?.display,
      }))
  }, [settings])

  const onAddNewAdjustment = useCallback(
    data => {
      const id = randomId()
      const decimals = determineNumberOfDecimals(data?.type, data?.unit, showWeightDecimals)
      const transformData = {
        ...data,
        amount: roundTo(data.amount, decimals),
        typeText: ADJUSTMENT_TYPES_ARRAY.find(type => type?.id === data?.type)?.name,
        descriptionText:
          listOfAdjustments?.current.find(description => description?.id === data?.description)?.name || '',
        unitText: FORMATTED_UNITS_ARRAY.find(unit => unit?.id === data?.unit)?.name,
      }
      apiRef.current.updateRows([{ id, ...transformData, isNew: true }])
      // Wait for the grid to render with the new row
      setTimeout(() => {
        apiRef.current.scrollToIndexes({
          rowIndex: apiRef.current.getRowsCount() - 1,
        })
      }, 150)
    },
    [apiRef, listOfAdjustments],
  )

  return (
    <DialogModal
      {...restProps}
      onSave={() => onSave([...apiRef.current.getRowModels().values()])}
      data-testid="adjustment-modal"
    >
      <Stack sx={{ height: 450, overflow: 'scroll' }}>
        <AddAdjustmentForm
          onSubmit={onAddNewAdjustment}
          listOfAdjustments={listOfAdjustments?.current}
          pricePrepend={pricePrepend}
        />
        <Box mt={3} py={3}>
          <Divider />
        </Box>
        <Typography gutterBottom>TOTAL ADJUSTMENT LIST</Typography>
        <BasicDataGrid
          density="compact"
          height="auto"
          rows={adjustments}
          columns={columns(showWeightDecimals, pricePrepend)}
          apiRef={apiRef}
          disableColumnMenu
          hideFooter
        />
      </Stack>
    </DialogModal>
  )
}

AdjustmentModal.propTypes = {
  ...DialogModal.propTypes,
  adjustments: PropTypes.array,
  pricePrepend: PropTypes.string,
}

AdjustmentModal.defaultProps = {
  ...DialogModal.defaultProps,
  title: 'Add a Adjustment',
  adjustments: [],
  pricePrepend: '$',
}

export default AdjustmentModal
